import Cookies from 'js-cookie'

import { buildPlatformCookieKeys, getSharedCookieDomain } from '@/utils/cookiePolicy'
import { removeCookieFromAllScopes, setHostCookie, setSharedCookie } from '@/utils/cookieStorage'

export interface StoredAccount {
  id: number
  name: string
  email: string
  token: string
  avatar?: string
  lastLoginTime: number
}

// Team token types and constants
export interface TeamTokenData {
  token: string
  teamId: number
  teamResID: string
  expiryTime: number
  userRole?: number
  userName?: string
}

import { TOKEN_CONFIG } from '@/consts/token'
export const TEAM_TOKEN_EXPIRY_MS = TOKEN_CONFIG.TEAM_TOKEN_EXPIRY_MS
export const TEAM_TOKEN_REFRESH_THRESHOLD_MS = TOKEN_CONFIG.TEAM_TOKEN_REFRESH_THRESHOLD_MS
export const LOGIN_TOKEN_EXPIRY_MS = TOKEN_CONFIG.LOGIN_TOKEN_EXPIRY_MS
export const LOGIN_TOKEN_COOKIE_EXPIRY_DAYS = TOKEN_CONFIG.LOGIN_TOKEN_COOKIE_EXPIRY_DAYS

// Cookie keys
const PLATFORM_COOKIE_KEYS = buildPlatformCookieKeys(import.meta.env.VITE_PLATFORM_COOKIE_NAMESPACE)
const TOKEN_KEY = PLATFORM_COOKIE_KEYS.token
const ACCOUNTS_KEY = PLATFORM_COOKIE_KEYS.accounts
const ACTIVE_ACCOUNT_ID_KEY = PLATFORM_COOKIE_KEYS.activeAccountId
const TEAM_TOKEN_DATA_KEY = 'glows_team_token_data'
const PRIMARY_TEAM_TOKEN_DATA_KEY = 'glows_primary_team_token_data'

// Legacy keys - kept for migration purposes
const LEGACY_TEAM_TOKEN_KEY = 'glows_team_token'
const LEGACY_TEAM_TOKEN_EXPIRY_KEY = 'glows_team_token_expiry'
const LEGACY_CURRENT_TEAM_ID_KEY = 'glows_current_team_id'
const LEGACY_CURRENT_TEAM_RES_ID_KEY = 'glows_current_team_res_id'

// Cookie expiry constants
const ACCOUNT_COOKIE_EXPIRY_DAYS = 30
// Cookie should live longer than token - allows reading teamId for refresh even after token expires
const TEAM_TOKEN_COOKIE_EXPIRY_DAYS = 1 // 1 day

// BroadcastChannel management
type AccountSyncMessage =
  | { type: 'ACCOUNT_ADDED'; accountId: number; timestamp: number }
  | { type: 'ACCOUNT_REMOVED'; accountId: number; timestamp: number }
  | { type: 'ACCOUNT_SWITCHED'; accountId: number; timestamp: number }
  | { type: 'ACCOUNTS_CLEARED'; timestamp: number }

class AccountSyncChannel {
  private channel: BroadcastChannel | null = null

  private getChannel(): BroadcastChannel | null {
    if (!('BroadcastChannel' in window)) return null
    if (!this.channel) {
      this.channel = new BroadcastChannel('glows-account-sync')
    }
    return this.channel
  }

  notify(message: AccountSyncMessage): void {
    const channel = this.getChannel()
    if (channel) {
      channel.postMessage(message)
    }
  }

  close(): void {
    if (this.channel) {
      this.channel.close()
      this.channel = null
    }
  }
}

const accountSync = new AccountSyncChannel()

// Validation helper
function isValidTeamTokenData(data: any): data is TeamTokenData {
  return (
    data &&
    typeof data.token === 'string' &&
    typeof data.teamId === 'number' &&
    typeof data.teamResID === 'string' &&
    typeof data.expiryTime === 'number'
  )
}

// Migration flag to avoid redundant checks
let migrationAttempted = false

export const getDomain = function (): string | false {
  return getSharedCookieDomain() ?? false
}

export function getToken() {
  return Cookies.get(TOKEN_KEY)
}

export function setToken(token: string, needSave: boolean): void {
  const options = needSave ? { expires: ACCOUNT_COOKIE_EXPIRY_DAYS } : {}
  setSharedCookie(TOKEN_KEY, token, options)
}

export function removeToken() {
  removeCookieFromAllScopes(TOKEN_KEY)
}

export function getStoredAccounts(): StoredAccount[] {
  try {
    const accountsStr = Cookies.get(ACCOUNTS_KEY)
    return accountsStr ? JSON.parse(accountsStr) : []
  } catch (error) {
    console.error('Error parsing stored accounts:', error)
    return []
  }
}

export function setStoredAccounts(accounts: StoredAccount[]): void {
  setSharedCookie(ACCOUNTS_KEY, JSON.stringify(accounts), {
    expires: ACCOUNT_COOKIE_EXPIRY_DAYS,
  })
}

export function getActiveAccountId(): number | null {
  const idStr = Cookies.get(ACTIVE_ACCOUNT_ID_KEY)
  return idStr ? parseInt(idStr, 10) : null
}

export function setActiveAccountId(accountId: number): void {
  setSharedCookie(ACTIVE_ACCOUNT_ID_KEY, accountId.toString(), {
    expires: ACCOUNT_COOKIE_EXPIRY_DAYS,
  })
}

export function addAccount(account: StoredAccount): void {
  const accounts = getStoredAccounts()
  const existingIndex = accounts.findIndex((acc) => acc.id === account.id)

  if (existingIndex >= 0) {
    accounts[existingIndex] = account
  } else {
    accounts.push(account)
  }

  setStoredAccounts(accounts)
  setActiveAccountId(account.id)
  setToken(account.token, true)

  // Notify other tabs about account addition
  accountSync.notify({
    type: 'ACCOUNT_ADDED',
    accountId: account.id,
    timestamp: Date.now(),
  })
}

export function removeAccount(accountId: number): void {
  const accounts = getStoredAccounts()
  const filteredAccounts = accounts.filter((acc) => acc.id !== accountId)
  setStoredAccounts(filteredAccounts)

  const activeId = getActiveAccountId()
  if (activeId === accountId) {
    if (filteredAccounts.length > 0) {
      const newActiveAccount = filteredAccounts[0]
      setActiveAccountId(newActiveAccount.id)
      setToken(newActiveAccount.token, true)
    } else {
      // Last account being removed - clear everything
      clearAllAccounts()
      return
    }
  }

  // Notify other tabs about account removal
  accountSync.notify({
    type: 'ACCOUNT_REMOVED',
    accountId,
    timestamp: Date.now(),
  })
}

export function switchToAccount(accountId: number): void {
  const accounts = getStoredAccounts()
  const targetAccount = accounts.find((acc) => acc.id === accountId)

  if (targetAccount) {
    setActiveAccountId(accountId)
    setToken(targetAccount.token, true)

    targetAccount.lastLoginTime = Date.now()
    setStoredAccounts(accounts)

    // Notify other tabs about account switch
    accountSync.notify({
      type: 'ACCOUNT_SWITCHED',
      accountId,
      timestamp: Date.now(),
    })
  }
}

export function getActiveAccount(): StoredAccount | null {
  const activeId = getActiveAccountId()
  if (!activeId) return null

  const accounts = getStoredAccounts()
  return accounts.find((acc) => acc.id === activeId) || null
}

export function removeActiveAccount(): void {
  removeCookieFromAllScopes(TOKEN_KEY)
  removeCookieFromAllScopes(ACTIVE_ACCOUNT_ID_KEY)
}

export function clearAllAccounts(): void {
  removeCookieFromAllScopes(ACCOUNTS_KEY)
  removeCookieFromAllScopes(ACTIVE_ACCOUNT_ID_KEY)
  removeCookieFromAllScopes(TOKEN_KEY)

  // Notify other tabs about accounts cleared
  accountSync.notify({
    type: 'ACCOUNTS_CLEARED',
    timestamp: Date.now(),
  })
}

export function migrateTokenToMultiAccount(userInfo: {
  id: number
  name: string
  email: string
}): boolean {
  const token = getToken()
  const accounts = getStoredAccounts()
  const activeAccountId = getActiveAccountId()

  // Only migrate if:
  // 1. We have a valid token
  // 2. No accounts exist in cookies
  // 3. No active account ID is set
  // 4. UserInfo has valid data
  if (token && userInfo.id > 0 && accounts.length === 0 && !activeAccountId) {
    // Double check that this account doesn't already exist
    const existingAccount = accounts.find((acc) => acc.id === userInfo.id)
    if (existingAccount) {
      return false
    }

    const newAccount: StoredAccount = {
      id: userInfo.id,
      name: userInfo.name,
      email: userInfo.email,
      token: token,
      lastLoginTime: Date.now(),
    }

    addAccount(newAccount)
    return true
  }

  return false
}

// Team token management functions (new unified API)
export function getTeamTokenData(): TeamTokenData | null {
  try {
    const dataStr = Cookies.get(TEAM_TOKEN_DATA_KEY)
    if (!dataStr) {
      // Try to migrate from legacy format (only once)
      if (!migrationAttempted) {
        migrationAttempted = true
        return migrateLegacyTeamToken()
      }
      return null
    }

    const parsed = JSON.parse(dataStr)

    // Validate data structure to prevent corrupted data
    if (!isValidTeamTokenData(parsed)) {
      console.error('Invalid team token data structure, clearing:', parsed)
      removeTeamTokenData()
      return null
    }

    return parsed
  } catch (error) {
    console.error('Error parsing team token data:', error)
    // Clear corrupted data
    removeTeamTokenData()
    return null
  }
}

export function setTeamTokenData(data: TeamTokenData, cookieExpiryDays?: number): void {
  try {
    if (!isValidTeamTokenData(data)) {
      throw new Error('Invalid team token data structure')
    }

    // Use fixed cookie expiry - cookie should outlive token to allow reading teamId for refresh
    setHostCookie(TEAM_TOKEN_DATA_KEY, JSON.stringify(data), {
      expires: cookieExpiryDays ?? TEAM_TOKEN_COOKIE_EXPIRY_DAYS,
    })
  } catch (error) {
    console.error('Failed to set team token data:', error)
    throw new Error(`Failed to set team token data: ${error}`)
  }
}

export function removeTeamTokenData(): void {
  removeCookieFromAllScopes(TEAM_TOKEN_DATA_KEY)
  // Also remove legacy cookies if they exist
  removeLegacyTeamTokenCookies()
}

// Primary Team Token management functions
export function getPrimaryTeamTokenData(): TeamTokenData | null {
  try {
    const dataStr = Cookies.get(PRIMARY_TEAM_TOKEN_DATA_KEY)
    if (!dataStr) {
      return null
    }

    const parsed = JSON.parse(dataStr)

    // Validate data structure to prevent corrupted data
    if (!isValidTeamTokenData(parsed)) {
      console.error('Invalid primary team token data structure, clearing:', parsed)
      removePrimaryTeamTokenData()
      return null
    }

    return parsed
  } catch (error) {
    console.error('Error parsing primary team token data:', error)
    // Clear corrupted data
    removePrimaryTeamTokenData()
    return null
  }
}

export function setPrimaryTeamTokenData(data: TeamTokenData, cookieExpiryDays?: number): void {
  try {
    if (!isValidTeamTokenData(data)) {
      throw new Error('Invalid primary team token data structure')
    }

    // Use fixed cookie expiry (7 days for primary token)
    setHostCookie(PRIMARY_TEAM_TOKEN_DATA_KEY, JSON.stringify(data), {
      expires: cookieExpiryDays ?? LOGIN_TOKEN_COOKIE_EXPIRY_DAYS,
    })
  } catch (error) {
    console.error('Failed to set primary team token data:', error)
    throw new Error(`Failed to set primary team token data: ${error}`)
  }
}

export function removePrimaryTeamTokenData(): void {
  removeCookieFromAllScopes(PRIMARY_TEAM_TOKEN_DATA_KEY)
}

// Legacy API - kept for backward compatibility (will be deprecated)
export function getTeamToken(): string | undefined {
  const data = getTeamTokenData()
  return data?.token
}

export function setTeamToken(token: string, teamId: number, teamResID: string): void {
  const expiryTime = Date.now() + TEAM_TOKEN_EXPIRY_MS
  setTeamTokenData({
    token,
    teamId,
    teamResID,
    expiryTime,
  })
}

export function removeTeamToken(): void {
  removeTeamTokenData()
}

export function getCurrentTeamId(): number | null {
  const data = getTeamTokenData()
  return data?.teamId || null
}

export function getCurrentTeamResID(): string | null {
  const data = getTeamTokenData()
  return data?.teamResID || null
}

export function setCurrentTeamId(teamId: number, teamResID: string): void {
  const existingData = getTeamTokenData()
  if (existingData) {
    setTeamTokenData({
      ...existingData,
      teamId,
      teamResID,
    })
  } else {
    // If no existing data, create minimal data structure (token will be set later)
    setTeamTokenData({
      token: '',
      teamId,
      teamResID,
      expiryTime: Date.now() + TEAM_TOKEN_EXPIRY_MS,
    })
  }
}

export function isTeamTokenExpiringSoon(): boolean {
  const data = getTeamTokenData()
  if (!data || !data.expiryTime) {
    return true
  }

  const remainingTime = data.expiryTime - Date.now()
  return remainingTime <= TEAM_TOKEN_REFRESH_THRESHOLD_MS
}

export function isTeamTokenExpired(): boolean {
  const data = getTeamTokenData()
  if (!data || !data.expiryTime) return true

  return Date.now() >= data.expiryTime
}

export function isTokenExpired(tokenData: TeamTokenData): boolean {
  if (!tokenData || !tokenData.expiryTime) return true
  return Date.now() >= tokenData.expiryTime
}

// Helper functions for migration and cleanup
function migrateLegacyTeamToken(): TeamTokenData | null {
  try {
    const token = Cookies.get(LEGACY_TEAM_TOKEN_KEY)
    const expiryStr = Cookies.get(LEGACY_TEAM_TOKEN_EXPIRY_KEY)
    const teamIdStr = Cookies.get(LEGACY_CURRENT_TEAM_ID_KEY)
    const teamResID = Cookies.get(LEGACY_CURRENT_TEAM_RES_ID_KEY)

    if (token && expiryStr && teamIdStr && teamResID) {
      const data: TeamTokenData = {
        token,
        teamId: parseInt(teamIdStr, 10),
        teamResID,
        expiryTime: parseInt(expiryStr, 10),
      }

      // Save to new format
      setTeamTokenData(data)

      // Remove legacy cookies
      removeLegacyTeamTokenCookies()

      return data
    }
  } catch (error) {
    console.error('Error migrating legacy team token:', error)
  }
  return null
}

function removeLegacyTeamTokenCookies(): void {
  const legacyKeys = [
    LEGACY_TEAM_TOKEN_KEY,
    LEGACY_TEAM_TOKEN_EXPIRY_KEY,
    LEGACY_CURRENT_TEAM_ID_KEY,
    LEGACY_CURRENT_TEAM_RES_ID_KEY,
  ]

  legacyKeys.forEach((key) => removeCookieFromAllScopes(key))
}
